import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import dayjs from 'dayjs'
import FadeIn from '../../../../components/molecules/Transitions/FadeIn'
import Message from './Message'
import NewThread from './NewThread'

const useStyles = makeStyles((theme) => ({
  messageList: {
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  }
}))

function MessageList ({ parentInfo, messages, isNewThread, onSuggestionClick, onRetry, isRetrying, onReply }) {
  const classes = useStyles()
  const sortedMessages = useMemo(() => {
    // Tag each message with its original index for stable fallback sorting
    // (threadMessageId is not monotonic, so we can't use it for ordering)
    const tagged = messages.map((msg, idx) => ({ msg, originalIndex: idx }))

    // Sort by createdDate, falling back to original API order when dates are equal/missing
    tagged.sort((a, b) => {
      if (a.msg?.createdDate && b.msg?.createdDate) {
        const dateA = dayjs(a.msg.createdDate)
        const dateB = dayjs(b.msg.createdDate)
        if (!dateA.isSame(dateB)) {
          return dateA.isBefore(dateB) ? -1 : 1
        }
      }
      // Preserve original API order when dates are equal or missing
      return a.originalIndex - b.originalIndex
    })

    return tagged.map(t => t.msg)
  }, [messages])

  if (isNewThread || messages.length === 0) {
    return (<NewThread onSuggestionClick={onSuggestionClick} />)
  }

  return (
    <FadeIn className={classes.messageList}>
      {sortedMessages.map((message, index) => (
        <Message
          key={message.threadMessageId || `placeholder-${index}`}
          parentInfo={parentInfo}
          message={message}
          onRetry={onRetry}
          isRetrying={isRetrying}
          onSuggestionClick={onSuggestionClick}
          onReply={onReply}
        />
      ))}
    </FadeIn>
  )
}

MessageList.propTypes = {
  parentInfo: PropTypes.shape({
    parentId: PropTypes.string,
    parentType: PropTypes.oneOf(['schedule', 'thread'])
  }),
  messages: PropTypes.arrayOf(PropTypes.object).isRequired,
  isNewThread: PropTypes.bool,
  onSuggestionClick: PropTypes.func,
  onRetry: PropTypes.func,
  isRetrying: PropTypes.bool,
  onReply: PropTypes.func
}

export default MessageList
